import { useState } from 'react';
import { api as axios } from '../utils/apiClient';

const buildKey = (siteId, slug) => {
  if (!siteId || !slug) return null;
  return `${siteId}:${slug}`;
};

export const useUpdatePlugin = (siteId) => {
  const [updatingTargets, setUpdatingTargets] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [lastResult, setLastResult] = useState(null);

  const updatePlugin = async (slug, options = {}, providedSiteId = null) => {
    const effectiveSiteId = providedSiteId || siteId;
    const { packageUrl, pluginFile, reason } = options || {};
    const timestamp = new Date().toISOString();
    
    console.log(`[useUpdatePlugin ${timestamp}] START`, {
      slug: slug,
      siteId: effectiveSiteId,
      packageUrl: packageUrl,
      pluginFile: pluginFile
    });
    
    const trimmedSlug = slug ? String(slug).trim() : '';
    if (!trimmedSlug) {
      const message = 'Plugin slug is required';
      setError(message);
      setLastResult({ success: false, error: message });
      return { success: false, error: message };
    }
    
    if (!effectiveSiteId) {
      const message = 'Site ID is required';
      setError(message);
      setLastResult({ success: false, error: message });
      return { success: false, error: message };
    }

    const targetKey = buildKey(effectiveSiteId, trimmedSlug);
    
    if (updatingTargets[targetKey]) {
      console.log(`[useUpdatePlugin ${timestamp}] Already updating`);
      return { success: false, error: 'Update already in progress' };
    }

    setUpdatingTargets((prev) => ({ ...prev, [targetKey]: true }));
    setError(null);
    setSuccess(null);
    setLastResult(null);

    try {
      const requestPayload = {
        slug: trimmedSlug,
      };
      
      if (packageUrl) {
        requestPayload.package_url = String(packageUrl).trim();
      }
      
      if (pluginFile) {
        requestPayload.plugin_file = String(pluginFile).trim();
      }
      
      if (reason) {
        requestPayload.reason = String(reason).trim();
      }
      
      console.log(`[useUpdatePlugin ${timestamp}] ===== FRONTEND REQUEST PAYLOAD =====`);
      console.log(`[useUpdatePlugin ${timestamp}] slug: "${trimmedSlug}" (type: ${typeof trimmedSlug}, length: ${trimmedSlug.length})`);
      console.log(`[useUpdatePlugin ${timestamp}] package_url: "${requestPayload.package_url || 'NOT PROVIDED'}"`);
      console.log(`[useUpdatePlugin ${timestamp}] plugin_file: "${requestPayload.plugin_file || 'NOT PROVIDED'}"`);
      console.log(`[useUpdatePlugin ${timestamp}] siteId: "${effectiveSiteId}"`);
      console.log(`[useUpdatePlugin ${timestamp}] Full payload JSON:`, JSON.stringify(requestPayload, null, 2));
      console.log(`[useUpdatePlugin ${timestamp}] POST URL: /api/sites/${effectiveSiteId}/update-plugin`);
      console.log(`[useUpdatePlugin ${timestamp}] ===== END FRONTEND REQUEST =====`);
      
      const response = await axios.post(
        `/api/sites/${effectiveSiteId}/update-plugin`,
        requestPayload,
        { 
          validateStatus: () => true,
          timeout: 120000
        }
      );
      
      const responseTimestamp = new Date().toISOString();
      console.log(`[useUpdatePlugin ${responseTimestamp}] ===== FRONTEND RESPONSE RECEIVED =====`);
      console.log(`[useUpdatePlugin ${responseTimestamp}] HTTP Status: ${response?.status} ${response?.statusText}`);
      console.log(`[useUpdatePlugin ${responseTimestamp}] Response data type: ${typeof response?.data}`);
      console.log(`[useUpdatePlugin ${responseTimestamp}] Response data.success: ${response?.data?.success} (type: ${typeof response?.data?.success})`);
      console.log(`[useUpdatePlugin ${responseTimestamp}] Response data.message: "${response?.data?.message || 'N/A'}"`);
      console.log(`[useUpdatePlugin ${responseTimestamp}] Response data.old_version: "${response?.data?.old_version || 'N/A'}"`);
      console.log(`[useUpdatePlugin ${responseTimestamp}] Response data.new_version: "${response?.data?.new_version || 'N/A'}"`);
      console.log(`[useUpdatePlugin ${responseTimestamp}] Full response JSON:`, JSON.stringify(response?.data, null, 2));
      console.log(`[useUpdatePlugin ${responseTimestamp}] ===== END FRONTEND RESPONSE =====`);

      const responseData = response.data || {};
      
      console.log(`[useUpdatePlugin ${responseTimestamp}] Checking success condition: responseData.success === true`);
      console.log(`[useUpdatePlugin ${responseTimestamp}] responseData.success value: ${responseData.success} (strict equality check: ${responseData.success === true})`);
      
      if (responseData.success === true) {
        console.log(`[useUpdatePlugin ${responseTimestamp}] SUCCESS CONDITION MET - Update succeeded`);
        const payload = {
          success: true,
          message: responseData.message || 'Plugin updated successfully',
          old_version: responseData.old_version,
          new_version: responseData.new_version,
          plugin_name: responseData.plugin_name,
          data: responseData,
          key: targetKey,
          status: response.status,
        };
        console.log(`[useUpdatePlugin ${responseTimestamp}] SUCCESS`, payload);
        setSuccess(payload);
        setLastResult(payload);
        
        // Trigger plugin list refresh event
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('plugin-updated', { 
            detail: { 
              slug: trimmedSlug, 
              siteId: effectiveSiteId,
              old_version: responseData.old_version,
              new_version: responseData.new_version
            } 
          }));
        }, 100);
        
        return payload;
      }

      // Handle 404 specifically - route not found
      if (response.status === 404) {
        const routeError = responseData.error === 'route_not_found' || 
                          responseData.code === 'rest_no_route' ||
                          (responseData.message && responseData.message.includes('No route was found'));
        
        if (routeError) {
          console.error(`[useUpdatePlugin ${responseTimestamp}] ===== CRITICAL: ROUTE NOT FOUND (404) =====`);
          console.error(`[useUpdatePlugin ${responseTimestamp}] The WordPress REST API endpoint does not exist.`);
          console.error(`[useUpdatePlugin ${responseTimestamp}] This means the WebMonitor plugin is NOT active on the WordPress site.`);
          console.error(`[useUpdatePlugin ${responseTimestamp}] Endpoint attempted: /wp-json/webmonitor/v1/update-plugin`);
          console.error(`[useUpdatePlugin ${responseTimestamp}] Troubleshooting:`, responseData.troubleshooting || 'N/A');
          console.error(`[useUpdatePlugin ${responseTimestamp}] ===== END 404 ERROR =====`);
        }
      }

      // Handle premium plugin download failure explicitly
      if (
        responseData.error_code === 'download_failed' ||
        responseData.code === 'download_failed'
      ) {
        console.error(
          `[useUpdatePlugin ${responseTimestamp}] Detected download_failed error – likely premium plugin without valid package_url`
        );
      }

      const errorMessage = responseData.message || 
                          responseData.error || 
                          responseData.code ||
                          `Update failed (HTTP ${response.status})`;
      
      console.error(`[useUpdatePlugin ${responseTimestamp}] ===== UPDATE FAILED =====`);
      console.error(`[useUpdatePlugin ${responseTimestamp}] HTTP Status: ${response.status}`);
      console.error(`[useUpdatePlugin ${responseTimestamp}] Error message: "${errorMessage}"`);
      console.error(`[useUpdatePlugin ${responseTimestamp}] responseData.success: ${responseData.success}`);
      console.error(`[useUpdatePlugin ${responseTimestamp}] responseData.code: ${responseData.code || 'N/A'}`);
      console.error(`[useUpdatePlugin ${responseTimestamp}] Full error data:`, JSON.stringify(responseData, null, 2));
      console.error(`[useUpdatePlugin ${responseTimestamp}] ===== END FAILURE =====`);
      
      const failure = {
        success: false,
        error: errorMessage,
        data: responseData,
        key: targetKey,
        status: response.status,
      };
      setError(errorMessage);
      setLastResult(failure);
      return failure;
      
    } catch (err) {
      const errorTimestamp = new Date().toISOString();
      console.error(`[useUpdatePlugin ${errorTimestamp}] EXCEPTION`, {
        message: err.message,
        response: err.response?.data ? JSON.stringify(err.response.data) : null,
        status: err.response?.status,
        stack: err.stack,
        timestamp: errorTimestamp
      });
      
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.error ||
                          err.message || 
                          'Failed to update plugin';
      
      const failure = {
        success: false,
        error: errorMessage,
        key: targetKey,
        httpStatus: err.response?.status,
        responseData: err.response?.data,
      };
      
      setError(errorMessage);
      setLastResult(failure);
      return failure;
    } finally {
      setUpdatingTargets((prev) => {
        const next = { ...prev };
        delete next[targetKey];
        return next;
      });
    }
  };

  const isUpdating = Object.keys(updatingTargets).length > 0;

  const isUpdatingTarget = (pluginSlug, providedSiteId = null) => {
    const effectiveSiteId = providedSiteId || siteId;
    if (!effectiveSiteId) return false;
    const trimmedSlug = pluginSlug ? String(pluginSlug).trim() : '';
    if (!trimmedSlug) return false;
    return Boolean(updatingTargets[buildKey(effectiveSiteId, trimmedSlug)]);
  };

  const clearMessages = () => {
    setError(null);
    setSuccess(null);
    setLastResult(null);
  };

  return {
    updatePlugin,
    isUpdating,
    isUpdatingTarget,
    updatingTargets,
    error,
    success,
    lastResult,
    clearMessages,
  };
};
